// Import / export des personnes via un fichier Excel ou CSV.
// Règle de fond : une cellule vide ne touche à rien (on peut donc envoyer un fichier partiel,
// par exemple seulement les fonctions). Pour effacer une valeur, écrire « - ».
import type { Brand, Person, PersonKind } from './types.ts';

export interface ImportColumn {
  header: string;
  field: ImportField | null;
}
export type ImportField =
  | 'primary_email'
  | 'kind'
  | 'first_name'
  | 'last_name'
  | 'display_name'
  | 'job_title'
  | 'phone_mobile'
  | 'phone_office'
  | 'brand_id'
  | 'managed'
  | 'active';

export interface ImportChange {
  label: string;
  from: string;
  to: string;
}
export interface ImportEntry {
  line: number; // numéro de ligne dans le fichier
  email: string;
  action: 'create' | 'update' | 'unchanged' | 'error';
  person?: Person;
  changes: ImportChange[];
  problems: string[];
  warnings: string[];
}
export interface ImportPlan {
  columns: ImportColumn[];
  entries: ImportEntry[];
  counts: { create: number; update: number; unchanged: number; error: number };
}

export const COLUMN_LABELS: Record<ImportField, string> = {
  primary_email: 'Adresse mail',
  kind: 'Type',
  first_name: 'Prénom',
  last_name: 'Nom',
  display_name: 'Nom affiché',
  job_title: 'Fonction',
  phone_mobile: 'Mobile',
  phone_office: 'Fixe',
  brand_id: 'Lieu (charte)',
  managed: 'Signature gérée',
  active: 'Active',
};

const EXPORT_ORDER: ImportField[] = [
  'primary_email',
  'kind',
  'first_name',
  'last_name',
  'display_name',
  'job_title',
  'phone_mobile',
  'phone_office',
  'brand_id',
  'managed',
  'active',
];

/** Compare les en-têtes sans tenir compte des accents, de la casse ni de la ponctuation. */
const normalize = (s: string): string =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const HEADER_ALIASES: [ImportField, string[]][] = [
  ['primary_email', [
    'adresse mail',
    'adresse',
    'adresse principale',
    'email',
    'e mail',
    'mail',
    'adresse email',
    'primary email',
  ]],
  ['kind', ['type', 'type de boite', 'kind']],
  ['first_name', ['prenom', 'first name', 'given name']],
  ['last_name', ['nom', 'nom de famille', 'last name', 'family name']],
  ['display_name', ['nom affiche', 'nom d affichage', 'display name']],
  ['job_title', ['fonction', 'poste', 'titre', 'intitule', 'job title', 'role']],
  ['phone_mobile', ['mobile', 'portable', 'telephone mobile', 'tel mobile', 'telephone', 'tel', 'gsm']],
  ['phone_office', ['fixe', 'telephone fixe', 'tel fixe', 'standard', 'ligne directe']],
  ['brand_id', ['lieu charte', 'lieu', 'charte', 'etablissement', 'site', 'marque', 'brand']],
  ['managed', ['signature geree', 'signature geree par l outil', 'geree', 'gere', 'managed']],
  ['active', ['active', 'actif', 'statut', 'status']],
];

export function mapHeaders(headers: string[]): ImportColumn[] {
  const used = new Set<ImportField>();
  return headers.map((header) => {
    const n = normalize(header);
    let field: ImportField | null = null;
    for (const [f, aliases] of HEADER_ALIASES) {
      if (used.has(f)) continue;
      if (aliases.includes(n)) {
        field = f;
        break;
      }
    }
    if (field) used.add(field);
    return { header, field };
  });
}

const CLEAR = '-';
const truthy = ['oui', 'o', 'yes', 'y', 'vrai', 'true', '1', 'x'];
const falsy = ['non', 'n', 'no', 'faux', 'false', '0'];

function parseBool(value: string): boolean | null {
  const n = normalize(value);
  if (truthy.includes(n)) return true;
  if (falsy.includes(n)) return false;
  return null;
}

function parseKind(value: string): PersonKind | null {
  const n = normalize(value);
  if (['personne', 'person', 'humain', 'salarie'].includes(n)) return 'person';
  if (['boite partagee', 'partagee', 'partage', 'shared', 'generique', 'boite generique'].includes(n)) return 'shared';
  return null;
}

function findBrand(value: string, brands: Brand[]): Brand | 'auto' | null {
  const n = normalize(value);
  if (['', 'auto', 'automatique', 'aucun', 'aucune'].includes(n)) return 'auto';
  return brands.find((b) => normalize(b.name) === n || normalize(b.id) === n) ?? null;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;

/**
 * Prépare l'import sans rien écrire : dit pour chaque ligne ce qui serait créé, modifié ou refusé.
 * `people` sert à repérer les doublons et les comptes absents de l'annuaire Google.
 */
export function planImport(rows: string[][], people: Person[], brands: Brand[]): ImportPlan {
  const headerRow = rows.findIndex((r) => r.some((c) => mapHeaders([c])[0].field === 'primary_email'));
  const columns = headerRow < 0 ? [] : mapHeaders(rows[headerRow]);
  const entries: ImportEntry[] = [];
  if (headerRow < 0) {
    return {
      columns: rows[0] ? mapHeaders(rows[0]) : [],
      entries: [{
        line: 1,
        email: '',
        action: 'error',
        changes: [],
        warnings: [],
        problems: ['Aucune colonne d’adresse mail trouvée. La première ligne doit contenir un en-tête « Adresse mail ».'],
      }],
      counts: { create: 0, update: 0, unchanged: 0, error: 1 },
    };
  }

  const byEmail = new Map(people.map((p) => [p.primary_email.toLowerCase(), p]));
  const seen = new Set<string>();
  const emailCol = columns.findIndex((c) => c.field === 'primary_email');

  for (let i = headerRow + 1; i < rows.length; i++) {
    const cells = rows[i];
    const email = (cells[emailCol] ?? '').trim().toLowerCase();
    const entry: ImportEntry = { line: i + 1, email, action: 'error', changes: [], problems: [], warnings: [] };
    if (!cells.some((c) => c !== '')) continue; // ligne vide

    if (!EMAIL_RE.test(email)) {
      entry.problems.push(email ? `Adresse invalide : « ${email} »` : 'Adresse mail manquante');
      entries.push(entry);
      continue;
    }
    if (seen.has(email)) {
      entry.problems.push('Adresse en double dans le fichier');
      entries.push(entry);
      continue;
    }
    seen.add(email);

    const existing = byEmail.get(email);
    const base: Person = existing ?? {
      id: '',
      primary_email: email,
      kind: 'person',
      first_name: null,
      last_name: null,
      display_name: null,
      job_title: null,
      phone_mobile: null,
      phone_office: null,
      brand_id: null,
      active: true,
      managed: true,
      no_banner: false,
      send_as_emails: [],
      local_last_seen_at: null,
      install_token: '',
      public_token: '',
    };
    const next: Person = { ...base };

    for (const [index, col] of columns.entries()) {
      const raw = (cells[index] ?? '').trim();
      if (!col.field || col.field === 'primary_email' || raw === '') continue;
      const label = COLUMN_LABELS[col.field];

      if (col.field === 'kind') {
        const kind = parseKind(raw);
        if (!kind) entry.problems.push(`${label} : « ${raw} » non reconnu (attendu : Personne ou Boîte partagée)`);
        else next.kind = kind;
        continue;
      }
      if (col.field === 'brand_id') {
        const brand = findBrand(raw, brands);
        if (!brand) {
          entry.problems.push(`${label} : « ${raw} » inconnu (chartes existantes : ${brands.map((b) => b.name).join(', ')})`);
        } else next.brand_id = brand === 'auto' ? null : brand.id;
        continue;
      }
      if (col.field === 'managed' || col.field === 'active') {
        const bool = parseBool(raw);
        if (bool === null) entry.problems.push(`${label} : « ${raw} » non reconnu (attendu : Oui ou Non)`);
        else next[col.field] = bool;
        continue;
      }
      next[col.field] = raw === CLEAR ? null : raw;
    }

    if (entry.problems.length) {
      entries.push(entry);
      continue;
    }

    const show = (field: ImportField, value: unknown): string => {
      if (field === 'brand_id') return value ? brands.find((b) => b.id === value)?.name ?? String(value) : 'Automatique';
      if (field === 'kind') return value === 'shared' ? 'Boîte partagée' : 'Personne';
      if (field === 'managed' || field === 'active') return value ? 'Oui' : 'Non';
      return value == null || value === '' ? '—' : String(value);
    };
    for (const field of EXPORT_ORDER) {
      if (field === 'primary_email') continue;
      if (base[field] !== next[field]) {
        entry.changes.push({ label: COLUMN_LABELS[field], from: show(field, base[field]), to: show(field, next[field]) });
      }
    }

    entry.person = next;
    entry.action = existing ? (entry.changes.length ? 'update' : 'unchanged') : 'create';
    if (!existing) {
      entry.warnings.push(
        'Compte absent de l’outil : il doit exister dans Google Workspace pour recevoir une signature (lancer la synchro de l’annuaire).',
      );
    }
    entries.push(entry);
  }

  const counts = { create: 0, update: 0, unchanged: 0, error: 0 };
  for (const e of entries) counts[e.action]++;
  return { columns, entries, counts };
}

// ---------------------------------------------------------------- export / modèle

export function peopleToRows(people: Person[], brands: Brand[]): string[][] {
  const header = EXPORT_ORDER.map((f) => COLUMN_LABELS[f]);
  const value = (p: Person, f: ImportField): string => {
    switch (f) {
      case 'kind':
        return p.kind === 'shared' ? 'Boîte partagée' : 'Personne';
      case 'brand_id':
        return p.brand_id ? brands.find((b) => b.id === p.brand_id)?.name ?? p.brand_id : 'Automatique';
      case 'managed':
        return p.managed ? 'Oui' : 'Non';
      case 'active':
        return p.active ? 'Oui' : 'Non';
      default:
        return (p[f] as string | null) ?? '';
    }
  };
  const rows = [...people]
    .sort((a, b) => a.primary_email.localeCompare(b.primary_email))
    .map((p) => EXPORT_ORDER.map((f) => value(p, f)));
  return [header, ...rows];
}

/** Modèle vierge avec une ligne d'exemple (à remplacer) quand il n'y a encore personne à exporter. */
export function templateRows(brands: Brand[]): string[][] {
  const example = [
    'exemple@bonjourbonsoir.paris',
    'Personne',
    'Prénom',
    'Nom',
    '',
    'Responsable booking',
    '06 12 34 56 78',
    '',
    brands[0]?.name ?? 'Automatique',
    'Oui',
    'Oui',
  ];
  return [EXPORT_ORDER.map((f) => COLUMN_LABELS[f]), example];
}
